import { FC, Fragment, ReactNode } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import { FormattedPrice } from 'dibs-buyer-price';
import {
    isStatsFilter,
    getFilterValues,
    constructStatsRange,
    isPriceFilter,
    isDimensionFilter,
} from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers';
import { CATEGORY_LEVEL_1, getCategories, getLowestCategory } from '../../utils/categoryHelpers';
import { useMeasurementUnit } from '../useMeasurementUnit';
import { SbRespRefineMenuDrawerAppliedFilterDisplay_itemSearch } from './__generated__/SbRespRefineMenuDrawerAppliedFilterDisplay_itemSearch.graphql';
import { SbRespRefineMenuDrawerAppliedFilterDisplay_user } from './__generated__/SbRespRefineMenuDrawerAppliedFilterDisplay_user.graphql';

type AppliedFilterValuesType = NonNullable<
    NonNullable<SbRespRefineMenuDrawerAppliedFilterDisplay_itemSearch['appliedFilters']>[0]
>['values'];

type Props = {
    itemSearch: SbRespRefineMenuDrawerAppliedFilterDisplay_itemSearch;
    user: SbRespRefineMenuDrawerAppliedFilterDisplay_user;
    currency: string;
    filterName: string;
};

// TODO: after resolving FINDING-14649 refactor sbSharedRefineMenuHelpers and SbRespAppliedFiltersItem to share this logic
const getStatsRangeComponents = ({
    appliedValue,
    appliedValueRange,
    currency,
    filterName,
    measurementUnit,
}: {
    appliedValue: string | null | undefined;
    appliedValueRange: string | null | undefined;
    currency: string;
    filterName: string;
    measurementUnit: string;
}): {
    min: ReactNode;
    max: ReactNode;
    dimension: ReactNode;
    separator: ReactNode;
} => {
    let hasWildcard;
    const [min, max] = constructStatsRange(appliedValue, appliedValueRange).map((stat, index) => {
        const isMin = index === 0;
        if (isNaN(stat)) {
            hasWildcard = true;
            return isMin ? (
                <FormattedMessage
                    id="SbRespRefineMenuDrawerAppliedFilterDisplay.underNumberRange"
                    defaultMessage="Under "
                    key={stat}
                />
            ) : (
                <FormattedMessage
                    id="SbRespRefineMenuDrawerAppliedFilterDisplay.overNumberRange"
                    defaultMessage=" and up"
                    key={stat}
                />
            );
        } else if (isPriceFilter(filterName)) {
            return <FormattedPrice currency={currency} value={stat} key={stat} />;
        } else {
            return <FormattedNumber value={stat} key={stat} />;
        }
    });
    return {
        min,
        max,
        dimension: isDimensionFilter(filterName) ? measurementUnit.toLowerCase() : '',
        separator: hasWildcard ? (
            ''
        ) : (
            <FormattedMessage
                id="SbRespRefineMenuDrawerAppliedFilterDisplay.numberRangeSeparator"
                defaultMessage=" to "
                key="separator"
            />
        ),
    };
};

const getHighestCategoryFilterValues = (
    appliedFilters: SbRespRefineMenuDrawerAppliedFilterDisplay_itemSearch['appliedFilters']
): AppliedFilterValuesType => {
    if (!appliedFilters) {
        return [];
    }
    const categoryFilters = getCategories([...appliedFilters]);
    const lowestCategory = getLowestCategory(categoryFilters);
    return lowestCategory?.values || [];
};

const SbRespRefineMenuDrawerAppliedFilterDisplayComponent: FC<Props> = ({
    user,
    itemSearch,
    currency,
    filterName,
}) => {
    const [measurementUnit] = useMeasurementUnit({ user, itemSearch });
    const { appliedFilters = [] } = itemSearch;
    if (!appliedFilters) {
        return null;
    }

    const appliedFilterValues: AppliedFilterValuesType =
        filterName === CATEGORY_LEVEL_1
            ? getHighestCategoryFilterValues(appliedFilters)
            : getFilterValues([...appliedFilters], filterName);
    if (!appliedFilterValues || !appliedFilterValues.length) {
        return null;
    }
    // TODO: after resolving FINDING-14649 refactor sbSharedRefineMenuHelpers and SbRespAppliedFiltersItem to share this logic
    if (isStatsFilter(filterName)) {
        const { min, max, separator, dimension } = getStatsRangeComponents({
            appliedValue: appliedFilterValues?.[0]?.urlLabel,
            appliedValueRange: appliedFilterValues?.[0]?.displayName,
            currency,
            filterName,
            measurementUnit,
        });
        return <Fragment>{[min, separator, max, dimension]}</Fragment>;
    } else {
        return (
            <Fragment>
                {appliedFilterValues
                    .map(v => v?.displayName)
                    .filter(Boolean)
                    .join(', ')}
            </Fragment>
        );
    }
};

export const SbRespRefineMenuDrawerAppliedFilterDisplay = createFragmentContainer(
    SbRespRefineMenuDrawerAppliedFilterDisplayComponent,
    {
        itemSearch: graphql`
            fragment SbRespRefineMenuDrawerAppliedFilterDisplay_itemSearch on ItemSearchQueryConnection {
                ...useMeasurementUnit_itemSearch
                appliedFilters {
                    name
                    values {
                        displayName
                        urlLabel
                    }
                }
            }
        `,
        user: graphql`
            fragment SbRespRefineMenuDrawerAppliedFilterDisplay_user on User {
                ...useMeasurementUnit_user
            }
        `,
    }
);
